import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import WizardCst from "@components/WizardCst";
import { WizardStep, ValidationResult } from "@components/WizardCst/types";
import { DynamicGroupWizardData } from "./types";
import DynGroupInfoStep from "./steps/DynGroupInfoStep";
import RulesStep from "./steps/RulesStep";
import DynEnrollStep from "./steps/DynEnrollStep";

const validateInfo = (data: DynamicGroupWizardData): ValidationResult => ({
  valid: !!data.name?.trim(),
  errors: data.name?.trim() ? {} : { name: "required" },
});

const validateRules = (data: DynamicGroupWizardData): ValidationResult => {
  const ok =
    data.sets.length > 0 &&
    data.sets.every(
      (s) =>
        s.rules.length > 0 &&
        s.rules.every((r) => r.condition_field && r.condition),
    );
  return { valid: ok, errors: ok ? {} : { sets: "rules-required" } };
};

interface DynamicGroupWizardProps {
  initialData: DynamicGroupWizardData;
  backTo: string;
  onComplete: (data: DynamicGroupWizardData) => void | Promise<void>;
  isCompleting?: boolean;
  title?: string;
  abandonMode?: "create" | "edit";
}

const DynamicGroupWizard: React.FC<DynamicGroupWizardProps> = ({
  initialData,
  backTo,
  onComplete,
  isCompleting,
  title,
  abandonMode = "create",
}) => {
  const { t } = useTranslation("group-wizard-v2");

  const steps = useMemo<WizardStep<DynamicGroupWizardData>[]>(
    () => [
      {
        key: "main-info",
        label: t("steps.mainInfo"),
        Component: DynGroupInfoStep,
        validate: validateInfo,
      },
      {
        key: "rules",
        label: t("steps.rules"),
        Component: RulesStep,
        validate: validateRules,
      },
      {
        key: "enroll",
        label: t("steps.enroll"),
        Component: DynEnrollStep,
      },
    ],
    [t],
  );

  return (
    <AnimationFadeIn>
      <Box sx={{ py: 3, pr: 3 }}>
        <WizardCst<DynamicGroupWizardData>
          title={title ?? t("titleCreateDynamic")}
          breadcrumb={<Breadcrumb />}
          backLink={{ label: t("backLink"), to: backTo }}
          steps={steps}
          initialData={initialData}
          onComplete={onComplete}
          abandonMode={abandonMode}
          abandonCopy={{
            title: String(
              t(
                abandonMode === "edit"
                  ? "abandon.edit-title"
                  : "abandon.create-title",
              ),
            ),
          }}
          nextLabel={t("next")}
          completeLabel={t("confirm")}
          backLabel={t("back")}
          resetLabel={t("reset")}
          isCompleting={isCompleting}
          resetConfirm={{
            title: t("resetConfirm.title"),
            description: t("resetConfirm.description"),
            confirmLabel: t("resetConfirm.confirm"),
            cancelLabel: t("resetConfirm.cancel"),
          }}
        />
      </Box>
    </AnimationFadeIn>
  );
};

export default DynamicGroupWizard;
